import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang, track } from "@/context/LanguageContext";
import { Reveal, MaskLines, ImageReveal } from "@/components/Reveal";
import { SectionLabel, Marquee } from "@/components/Editorial";
import { CTABand } from "@/components/Shared";
import { IMAGES, BSPORT, PRICING, TESTIMONIALS } from "@/data/config";
import axios from "axios";
import { API } from "@/lib/api";
import { Users, Award, MapPin } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const WHY_ICONS = [Users, Award, MapPin];

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-onyx" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.studioInterior} alt="Maison Core studio interior in Amsterdam" className="h-[118%] w-full object-cover" />
        <div className="absolute inset-0 bg-onyx/45" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-onyx/70 to-transparent" />
      </motion.div>

      <motion.div
        className="absolute right-6 top-28 hidden md:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
      >
        <span className="label writing-mode-vertical text-blush [writing-mode:vertical-rl]">Contrôle · Ligne · Force</span>
      </motion.div>

      <div className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}>
          <SectionLabel light>Reformer Pilates · Amsterdam</SectionLabel>
        </motion.div>
        <h1 className="mt-6 font-serif text-[19vw] leading-[0.82] text-petal md:text-[13vw] lg:text-[11rem]">
          <MaskLines lines={[t.home.heroLine1, t.home.heroLine2, t.home.heroLine3]} lineClassName="italic first:not-italic" />
        </h1>
        <motion.div
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
        >
          <p className="max-w-md font-sans text-base leading-relaxed text-petal/85">{t.home.heroSub}</p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href={BSPORT.INTRO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("intro_offer_click", { source: "hero" })}
              className="label bg-petal px-9 py-4 text-onyx transition-colors duration-500 hover:bg-terracotta hover:text-petal"
              data-testid="hero-intro"
            >
              {t.common.startIntro}
            </a>
            <Link
              to="/schedule"
              onClick={() => track("view_schedule_click", { source: "hero" })}
              className="label border border-petal/50 px-9 py-4 text-petal transition-colors duration-500 hover:border-petal"
              data-testid="hero-schedule"
            >
              {t.common.viewSchedule}
            </Link>
            <span className="label text-blush">{t.common.threeForFiftyNine}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function IntroOffer() {
  const { t } = useLang();
  return (
    <section className="bg-rust text-petal" data-testid="intro-offer">
      <div className="mx-auto grid max-w-[1600px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-36">
        <div className="md:col-span-7">
          <SectionLabel light>{t.home.introLabel}</SectionLabel>
          <Reveal><h2 className="mt-8 font-serif text-6xl leading-[0.95] md:text-8xl">{t.home.introTitle}</h2></Reveal>
          <p className="mt-8 max-w-lg font-sans text-lg leading-relaxed text-petal/80">{t.home.introBody}</p>
          <p className="mt-6 max-w-lg font-sans text-sm leading-relaxed text-petal/60" data-testid="home-intro-settle">{t.common.introSettle}</p>
        </div>
        <div className="flex flex-col justify-between gap-10 border-t border-petal/20 pt-10 md:col-span-4 md:col-start-9 md:border-l md:border-t-0 md:pl-12 md:pt-0">
          <div>
            <p className="font-serif text-3xl italic text-blush">{t.pricing.introTitle}</p>
            <p className="font-serif text-8xl leading-none">{PRICING.intro.price}</p>
          </div>
          <a
            href={BSPORT.INTRO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("intro_offer_click", { source: "home-intro" })}
            className="label border border-petal bg-petal py-5 text-center text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal"
            data-testid="intro-cta"
          >
            {t.common.startIntro}
          </a>
        </div>
      </div>
    </section>
  );
}

function Method() {
  const { t } = useLang();
  return (
    <section className="bg-petal" data-testid="method">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <SectionLabel>{t.home.methodLabel}</SectionLabel>
            <Reveal><h2 className="mt-8 font-serif text-6xl leading-[0.95] md:text-7xl">{t.home.methodTitle}</h2></Reveal>
            <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-onyx/70">{t.home.methodBody}</p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            {t.home.methodItems.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.08}>
                <div className="flex items-baseline justify-between gap-8 border-b border-onyx/15 py-8">
                  <h3 className="font-serif text-4xl italic md:text-5xl">{m.t}</h3>
                  <p className="max-w-xs text-right font-sans text-sm leading-relaxed text-onyx/60">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <Marquee />
    </section>
  );
}

function ClassExperience() {
  const { t } = useLang();
  return (
    <section className="bg-petal" data-testid="class-experience">
      <div className="mx-auto grid max-w-[1600px] items-stretch gap-0 md:grid-cols-2">
        <ImageReveal src={IMAGES.movement1} alt="Reformer carriage detail" className="aspect-[4/5] md:aspect-auto md:h-full" />
        <div className="flex flex-col justify-center px-6 py-20 md:px-16">
          <SectionLabel>{t.home.classLabel}</SectionLabel>
          <Reveal><h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-7xl">{t.home.classTitle}</h2></Reveal>
          <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-onyx/70">{t.home.classBody}</p>
          <Link to="/classes" className="label mt-10 inline-block w-fit border-b border-onyx pb-1 transition-opacity hover:opacity-60" data-testid="home-explore-classes">
            {t.common.exploreClasses} →
          </Link>
        </div>
      </div>
    </section>
  );
}

function Studio() {
  const { t } = useLang();
  return (
    <section className="bg-petal" data-testid="studio">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
        <div className="max-w-2xl">
          <SectionLabel>{t.home.studioLabel}</SectionLabel>
          <Reveal><h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-7xl">{t.home.studioTitle}</h2></Reveal>
          <p className="mt-8 font-sans text-lg leading-relaxed text-onyx/70">{t.home.studioBody}</p>
        </div>
        <div className="mt-16 grid grid-cols-12 gap-4 md:gap-8">
          <ImageReveal src={IMAGES.arch2} alt="Studio architecture" className="col-span-12 aspect-[16/10] md:col-span-7" />
          <ImageReveal src={IMAGES.detail} alt="Studio material detail" className="col-span-6 aspect-[3/4] md:col-span-5 md:mt-20" />
          <ImageReveal src={IMAGES.arch1} alt="Studio interior" className="col-span-6 aspect-[3/4] md:col-span-4 md:col-start-2" />
          <ImageReveal src={IMAGES.studioWide} alt="Studio reception" className="col-span-12 aspect-[16/9] md:col-span-6" />
        </div>
      </div>
    </section>
  );
}

function WhyMaisonCore() {
  const { t } = useLang();
  return (
    <section className="bg-blush/40" data-testid="why-maisoncore">
      <div className="mx-auto grid max-w-[1600px] items-stretch gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
        <div className="md:col-span-6">
          <SectionLabel>{t.home.whyLabel}</SectionLabel>
          <Reveal><h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-7xl">{t.home.whyTitle}</h2></Reveal>
          <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-onyx/70">{t.home.whyBody}</p>
          <div className="mt-12 border-t border-onyx/15">
            {t.home.whyItems.map((it, i) => {
              const Icon = WHY_ICONS[i] || Users;
              return (
                <Reveal key={it.t} delay={i * 0.08}>
                  <div className="flex gap-5 border-b border-onyx/15 py-6" data-testid={`why-item-${i}`}>
                    <Icon size={24} strokeWidth={1.3} className="mt-1 shrink-0 text-terracotta" />
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl">{it.t}</h3>
                      <p className="mt-2 font-sans text-base leading-relaxed text-onyx/65">{it.b}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <ImageReveal src={IMAGES.arch1} alt="Maison Core boutique Reformer studio in Amsterdam" className="aspect-[4/5] w-full md:aspect-auto md:h-full md:min-h-[560px]" />
        </div>
      </div>
    </section>
  );
}

function Memberships() {
  const { t } = useLang();
  return (
    <section className="bg-onyx text-petal" data-testid="memberships">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-36">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <SectionLabel light>{t.home.memLabel}</SectionLabel>
            <Reveal><h2 className="mt-8 font-serif text-6xl leading-[0.95] md:text-8xl">{t.home.memTitle}</h2></Reveal>
          </div>
          <Link to="/pricing" className="label border-b border-petal/50 pb-1 transition-opacity hover:opacity-70" data-testid="home-explore-memberships">
            {t.common.exploreMemberships} →
          </Link>
        </div>
        <p className="label mt-6 text-blush">{t.home.memNote}</p>
        <div className="mt-12 border-t border-petal/15">
          {PRICING.memberships.map((m, i) => (
            <Reveal key={m.key} delay={i * 0.06}>
              <div className={`grid grid-cols-12 items-center gap-4 border-b border-petal/15 py-8 ${m.featured ? "text-petal" : ""}`}>
                <div className="col-span-12 flex items-baseline gap-4 md:col-span-5">
                  <h3 className="font-serif text-4xl md:text-5xl">{m.name}</h3>
                  {m.featured && <span className="label text-terracotta">·  balanced</span>}
                </div>
                <p className="col-span-6 label text-blush md:col-span-3">{m.freq}</p>
                <p className="col-span-6 md:col-span-2 font-serif text-3xl md:text-4xl"><span className="mr-1 font-sans text-xs text-petal/50">{t.common.from}</span>{m.price}<span className="ml-1 font-sans text-xs text-petal/50">{t.common.per4weeks}</span></p>
                <div className="col-span-12 md:col-span-2 md:text-right">
                  <Link to="/pricing" onClick={() => track("membership_click", { plan: m.key, source: "home" })} className="label border-b border-petal/50 pb-1 transition-opacity hover:opacity-70" data-testid={`home-membership-${m.key}`}>
                    {t.pricing.choose} →
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const total = TESTIMONIALS.length;

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setActive((a) => (a + 1) % total), 7000);
    return () => clearInterval(id);
  }, [reduce, total]);

  const tm = TESTIMONIALS[active];
  const note = tm.original !== lang ? (lang === "en" ? t.home.translatedNote : t.home.translatedNote) : null;

  return (
    <section className="bg-petal" data-testid="testimonials">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <SectionLabel>{t.home.proofLabel}</SectionLabel>
          <span className="label text-clay">{t.home.reviewLabel}</span>
        </div>

        <div className="mt-12 min-h-[320px] md:mt-16 md:min-h-[380px]">
          <AnimatePresence mode="wait">
            <motion.figure
              key={active}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <blockquote className="max-w-5xl font-serif text-3xl font-light leading-[1.15] text-onyx md:text-5xl lg:text-6xl">
                {tm[lang]}
              </blockquote>
              <figcaption className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="label text-terracotta">{tm.name}</span>
                {note && <span className="font-sans text-xs italic text-onyx/40">— {note}</span>}
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-onyx/15 pt-8">
          {TESTIMONIALS.map((item, i) => (
            <button
              key={item.name}
              onClick={() => setActive(i)}
              data-testid={`testimonial-tab-${i}`}
              className={`label pb-1 transition-all duration-300 ${
                i === active ? "border-b border-onyx text-onyx" : "border-b border-transparent text-onyx/35 hover:text-onyx/70"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsPreview() {
  const { t } = useLang();
  return (
    <section className="bg-blush/40" data-testid="events-preview">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-6 py-24 md:grid-cols-2 md:px-12 md:py-32">
        <div>
          <SectionLabel>{t.home.eventsLabel}</SectionLabel>
          <Reveal><h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-7xl">{t.home.eventsTitle}</h2></Reveal>
          <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-onyx/70">{t.home.eventsBody}</p>
          <Link to="/events" className="label mt-10 inline-block border-b border-onyx pb-1 transition-opacity hover:opacity-60" data-testid="home-discover-events">
            {t.common.discoverEvents} →
          </Link>
        </div>
        <ImageReveal src={IMAGES.equipBall} alt="Pilates ball and magic circle" className="aspect-[4/3]" />
      </div>
    </section>
  );
}

function TodayClasses() {
  const { lang } = useLang();
  const c = lang === "nl"
    ? { label: "Vandaag in de studio", tomorrow: "Morgen in de studio", book: "Boek", full: "Vol", left: (n) => `Nog ${n} ${n === 1 ? "plek" : "plekken"}`, all: "Bekijk het volledige rooster →" }
    : { label: "Today at the studio", tomorrow: "Tomorrow at the studio", book: "Book", full: "Full", left: (n) => `${n} ${n === 1 ? "spot" : "spots"} left`, all: "View the full schedule →" };
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState(c.label);

  useEffect(() => {
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const now = new Date();
    const today = iso(now);
    axios.get(`${API}/schedule`, { params: { start: today, end: today } })
      .then((r) => {
        const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        const upcoming = r.data.filter((s) => s.start.slice(11, 16) > nowHM).slice(0, 3);
        if (upcoming.length) { setItems(upcoming); setLabel(c.label); return; }
        const tm = new Date(now); tm.setDate(tm.getDate() + 1);
        const t = iso(tm);
        return axios.get(`${API}/schedule`, { params: { start: t, end: t } }).then((r2) => {
          setItems(r2.data.slice(0, 3));
          setLabel(c.tomorrow);
        });
      })
      .catch(() => setItems([]));
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!items.length) return null;
  return (
    <section className="bg-petal" data-testid="home-today">
      <div className="mx-auto max-w-[1600px] px-6 py-14 md:px-12">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-t border-onyx/15 pt-8">
          <SectionLabel>{label}</SectionLabel>
          <Link to="/schedule" onClick={() => track("view_schedule_click", { source: "home-today" })} className="label border-b border-onyx pb-1 hover:opacity-60" data-testid="home-today-all">{c.all}</Link>
        </div>
        <div className="mt-8">
          {items.map((s) => (
            <div key={s.id} className="flex flex-wrap items-baseline justify-between gap-4 border-b border-onyx/10 py-5" data-testid={`home-today-class-${s.id}`}>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <span className="label text-terracotta">{s.start.slice(11, 16)}</span>
                <span className="font-serif text-2xl md:text-3xl">{s.name}</span>
                {s.coach && <span className="font-sans text-sm text-onyx/55">{s.coach}</span>}
              </div>
              {s.full ? (
                <span className="label text-onyx/40">{c.full}</span>
              ) : (
                <span className="flex items-center gap-5">
                  {s.spots_left != null && s.spots_left > 0 && s.spots_left <= 3 && (
                    <span className="label text-terracotta" data-testid={`home-today-spots-${s.id}`}>{c.left(s.spots_left)}</span>
                  )}
                  <a
                  href={`https://backoffice.bsport.io/customer/payment/offer/${s.id}/?membership=6040&force=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("book_class_click", { source: "home-today" })}
                  className="label border border-onyx px-6 py-3 transition-colors duration-500 hover:bg-onyx hover:text-petal"
                  data-testid={`home-today-book-${s.id}`}
                >
                  {c.book} →
                </a>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorporateBand() {
  const { lang } = useLang();
  const c = lang === "nl"
    ? { label: "Maison Core for Business", line: "Privé Reformer Pilates voor teams, bedrijven en organisaties.", cta: "Ontdek corporate →" }
    : { label: "Maison Core for Business", line: "Private Reformer Pilates for teams, companies and organisations.", cta: "Discover corporate →" };
  return (
    <section className="bg-blush/40" data-testid="home-corporate">
      <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-6 px-6 py-14 md:flex-row md:items-center md:px-12">
        <div>
          <SectionLabel>{c.label}</SectionLabel>
          <p className="mt-5 max-w-xl font-serif text-2xl leading-snug text-onyx md:text-3xl">{c.line}</p>
        </div>
        <Link
          to="/corporate"
          onClick={() => track("corporate_home_click")}
          className="label shrink-0 border-b border-onyx pb-1 transition-opacity hover:opacity-60"
          data-testid="home-corporate-cta"
        >
          {c.cta}
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const { lang } = useLang();
  usePageMeta(
    lang === "nl"
      ? {
          title: "Maison Core — Reformer Pilates, verfijnd. Amsterdam",
          description: "Boetiek Reformer Pilates in Amsterdam. Beweeg met intentie — kracht, houding en controle. Start je intro: 3 lessen voor €59.",
          canonical: "https://maisoncore.nl/",
        }
      : {
          title: "Maison Core — Reformer Pilates, refined. Amsterdam",
          description: "Boutique Reformer Pilates in Amsterdam. Move with intention — strength, alignment and control. Start your intro: 3 classes for €59.",
          canonical: "https://maisoncore.nl/",
        }
  );
  return (
    <>
      <Hero />
      <TodayClasses />
      <IntroOffer />
      <WhyMaisonCore />
      <Method />
      <ClassExperience />
      <Studio />
      <Testimonials />
      <Memberships />
      <CorporateBand />
      <EventsPreview />
      <CTABand />
    </>
  );
}
